import type { ChatLabels } from "./types.js";

/**
 * What the AI Platform calls a chat before its summarization prompt has named it. Mirrors
 * PlaceholderTitles in AiChatService; the backend already swaps these for the first question,
 * so this is the guard for the window before that lands and for a failed lookup.
 */
const PLACEHOLDER_TITLES = new Set(["chat", "nieuwe chat", "new chat"]);

/** Keeps a question-derived title close in length to the platform's 4-5 word summaries. */
const QUESTION_TITLE_MAX_LENGTH = 80;

function getLabel(labels: ChatLabels | undefined, key: string, fallback: string): string {
  const value = labels?.[key];
  return typeof value === "string" && value.trim() ? value : fallback;
}

export function isPlaceholderTitle(title: string | undefined): boolean {
  const trimmed = (title ?? "").trim();
  return trimmed === "" || PLACEHOLDER_TITLES.has(trimmed.toLowerCase());
}

/** Turns a question into a one-line title, so a pasted multi-line question stays readable. */
export function questionTitle(text: string | undefined): string {
  const collapsed = (text ?? "").replace(/\s+/g, " ").trim();
  if (collapsed.length <= QUESTION_TITLE_MAX_LENGTH) return collapsed;
  return `${collapsed.slice(0, QUESTION_TITLE_MAX_LENGTH).trimEnd()}\u2026`;
}

/**
 * Display title for a conversation history entry (the AI Platform title). Strips the
 * leading "YYYY-MM-DD HH:MM - " timestamp prefix (the grouped date headers already
 * convey recency) and falls back to the localized default title when empty. Shared by
 * the conversations panel list and the thread header so both always show the same title.
 *
 * Legacy entries carry that prefix followed by the entire first message, newlines and all,
 * so the remainder runs through the same one-line shortening as a question-derived title.
 */
export function historyEntryTitle(
  rawTitle: string | undefined,
  labels: ChatLabels | undefined,
): string {
  const cleaned = questionTitle(
    (rawTitle ?? "").replace(/^\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}\s*-\s*/, ""),
  );
  return cleaned || getLabel(labels, "defaultHistoryTitle", "Jonas gesprek");
}
